import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IAppBus } from '@/services/AppBus';
import { type ICsvParser, type CsvParseResult } from '@/services/CsvParser';
import { type IColumnMapper } from '@/services/ColumnMapper';
import { type ILogger } from '@/services/Logger';
import { type SourceKind, type QuarantineRow } from '@/domain/beforered';
import { type LocaleStore } from '../LocaleStore';

export type ImportSlotState = 'empty' | 'parsing' | 'mapping' | 'valid' | 'error';

export class ImportSlotStore {
  private _source: SourceKind = 'BOM';
  private _filename = '';
  private _state: ImportSlotState = 'empty';
  private _parsed: CsvParseResult | null = null;
  private _mapping: Record<string, string | null> = {};
  private _quarantine: QuarantineRow[] = [];
  private _errorMessage = '';
  // Stale-drop guard: a re-drop bumps this; the older parse's writes check `isCurrent`.
  private _dropGen = 0;

  constructor(
    private bus: IAppBus,
    private csv: ICsvParser,
    private mapper: IColumnMapper,
    private logger: ILogger,
    private locale: LocaleStore,
  ) {
    makeAutoObservable(this);
  }

  init(source: SourceKind): void {
    this._source = source;
  }

  get source(): SourceKind {
    return this._source;
  }
  get filename(): string {
    return this._filename;
  }
  get state(): ImportSlotState {
    return this._state;
  }
  get parsed(): CsvParseResult | null {
    return this._parsed;
  }
  get mapping(): Readonly<Record<string, string | null>> {
    return this._mapping;
  }
  get rowCount(): number {
    return this._parsed?.rows.length ?? 0;
  }
  get errorMessage(): string {
    return this._errorMessage;
  }
  get quarantine(): readonly QuarantineRow[] {
    return this._quarantine;
  }

  get fields() {
    return this.mapper.spec(this._source);
  }

  get unmappedRequired(): string[] {
    return this.fields
      .filter((f) => f.required)
      .map((f) => f.name)
      .filter((name) => !this._mapping[name]);
  }

  get isValid(): boolean {
    return this._state === 'valid';
  }

  reset = (): void => {
    this._dropGen++;
    this._filename = '';
    this._state = 'empty';
    this._parsed = null;
    this._mapping = {};
    this._quarantine = [];
    this._errorMessage = '';
  };

  drop = async (file: File): Promise<void> => {
    this.reset();
    const gen = this._dropGen;
    const isCurrent = (): boolean => gen === this._dropGen;
    this._filename = file.name;
    this._state = 'parsing';
    try {
      const parsed = await this.csv.parse(file);
      if (!isCurrent()) return;
      const suggestion = this.mapper.suggest(parsed.headers, this._source);
      this._parsed = parsed;
      this._mapping = { ...suggestion };
      this._state = 'mapping';
      await this.bus.importCompleted({
        source: this._source,
        filename: file.name,
        rowCount: parsed.rows.length,
      });
      if (!isCurrent()) return;
      this.confirmIfReady();
    } catch (cause) {
      this.logger.error('ImportSlotStore.drop failed', cause);
      if (!isCurrent()) return;
      this._state = 'error';
      this._errorMessage = cause instanceof Error ? cause.message : String(cause);
    }
  };

  setMapping = (canonical: string, header: string | null): void => {
    this._mapping = { ...this._mapping, [canonical]: header };
    this.confirmIfReady();
  };

  confirm = (): void => {
    if (this.unmappedRequired.length > 0) {
      const t = this.locale.messages.toasts;
      this.bus.toast('error', `${t.mapAllFirst} (${this.unmappedRequired.join(', ')})`);
      return;
    }
    this._state = 'valid';
    this.bus.mappingConfirmed({ source: this._source, mapping: { ...this._mapping } });
  };

  private confirmIfReady(): void {
    if (this.unmappedRequired.length === 0 && this._state === 'mapping') {
      this._state = 'valid';
      this.bus.mappingConfirmed({ source: this._source, mapping: { ...this._mapping } });
    }
  }

  addQuarantineRow(row: QuarantineRow): void {
    this._quarantine = [...this._quarantine, row];
  }
}

export const TOKEN_ImportSlotStore = token<ImportSlotStore>('ImportSlotStore');
export type ImportSlotFactory = () => ImportSlotStore;
